import { useEffect, useState } from 'react';

const TICK_MS = 1_000;

/**
 * Formats a whole-second duration as `m:ss`, or `h:mm:ss` past the hour.
 *
 * @param totalSeconds - Elapsed seconds.
 * @returns The formatted duration.
 */
export function formatElapsed(totalSeconds: number): string {
	const seconds = totalSeconds % 60;
	const minutes = Math.floor(totalSeconds / 60) % 60;
	const hours = Math.floor(totalSeconds / 3600);
	const paddedSeconds = String(seconds).padStart(2, '0');
	return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}` : `${minutes}:${paddedSeconds}`;
}

/**
 * Counts whole seconds since `since`, ticking once a second. Falls back to the
 * caller's mount time when no start is known, which understates anything that
 * began before this component appeared.
 *
 * @param since - Epoch milliseconds the count runs from, or null to use mount time.
 * @returns Seconds elapsed, starting at zero.
 */
export function useElapsedSeconds(since: number | null = null): number {
	const [mountedAt] = useState(() => Date.now());
	const startedAt = since ?? mountedAt;
	const [elapsedSeconds, setElapsedSeconds] = useState(0);

	// Driving an interval — the justified use of an effect.
	useEffect(() => {
		const timer = setInterval(() => {
			setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
		}, TICK_MS);
		return () => clearInterval(timer);
	}, [startedAt]);

	return elapsedSeconds;
}
